// Course preview and lesson chrome share one visibility/fullscreen state per card.
// The video provider keeps its own controls; this module never handles media seeking.
const coursePlayerStates = new WeakMap();

function attachCoursePlayer(root, player) {
  if (!root || !player) return;
  disconnectCoursePlayer(root);

  const listeners = [];
  const state = { player, listeners, timer: null, observer: null, playing: !player.paused, expanded: false };
  coursePlayerStates.set(root, state);
  root.classList.add("controls-visible");

  function listen(target, event, handler) {
    target.addEventListener(event, handler);
    listeners.push([target, event, handler]);
  }

  function cancelHide() {
    clearTimeout(state.timer);
    state.timer = null;
  }

  function hasOpenPanel() {
    return !!root.querySelector("[data-course-player-panel]:not([hidden])") ||
      !!root.querySelector(".modal.show");
  }

  function scheduleHide() {
    cancelHide();
    if (!state.playing || hasOpenPanel() || document.hidden) return;
    state.timer = setTimeout(function () {
      if (state.playing && !hasOpenPanel() && !root.querySelector(":focus-visible")) {
        root.classList.remove("controls-visible");
      }
    }, 3000);
  }

  function reveal() {
    root.classList.add("controls-visible");
    scheduleHide();
  }

  function onPlay() {
    state.playing = true;
    root.classList.add("is-playing");
    reveal();
  }

  function onPause() {
    state.playing = false;
    root.classList.remove("is-playing");
    cancelHide();
    root.classList.add("controls-visible");
  }

  player.on("play", onPlay);
  player.on("pause", onPause);
  player.on("ended", onPause);
  player.on("error", onPause);
  state.playerListeners = [["play", onPlay], ["pause", onPause], ["ended", onPause], ["error", onPause]];

  listen(root, "pointermove", reveal);
  listen(root, "pointerdown", function (event) {
    if (event.target.closest("[data-course-player-media]") &&
        !event.target.closest("button, a, input, select, textarea, .plyr__controls")) {
      reveal();
    }
  });
  listen(root, "focusin", function () { root.classList.add("controls-visible"); cancelHide(); });
  listen(root, "focusout", function () { setTimeout(scheduleHide, 0); });
  listen(root, "show.bs.modal", function () { root.classList.add("controls-visible"); cancelHide(); });
  listen(root, "hidden.bs.modal", reveal);

  const revealButton = root.querySelector("[data-course-player-reveal]");
  if (revealButton) listen(revealButton, "click", reveal);

  function closePanel() {
    const panel = root.querySelector("[data-course-player-panel]:not([hidden])");
    if (!panel) return;
    panel.hidden = true;
    root.querySelectorAll('[data-course-player-open="' + panel.dataset.coursePlayerPanel + '"]').forEach(function (button) {
      button.setAttribute("aria-expanded", "false");
    });
    reveal();
    if (state.panelTrigger) state.panelTrigger.focus();
    state.panelTrigger = null;
  }

  listen(root, "click", function (event) {
    const opener = event.target.closest("[data-course-player-open]");
    if (opener && root.contains(opener)) {
      const panel = root.querySelector('[data-course-player-panel="' + opener.dataset.coursePlayerOpen + '"]');
      if (!panel) return;
      closePanel();
      state.panelTrigger = opener;
      panel.hidden = false;
      root.querySelectorAll('[data-course-player-open="' + opener.dataset.coursePlayerOpen + '"]').forEach(function (button) {
        button.setAttribute("aria-expanded", "true");
      });
      cancelHide();
      root.classList.add("controls-visible");
      panel.querySelector(".course-player-panel-close").focus();
      return;
    }
    const closer = event.target.closest("[data-course-player-close]");
    if (closer && root.contains(closer)) closePanel();
  });
  listen(root, "click", async function (event) {
    const button = event.target.closest("[data-course-player-copy]");
    if (!button || !root.contains(button)) return;
    const panel = button.closest('[data-course-player-panel="share"]');
    const input = panel.querySelector(".course-player-share-url");
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(input.value);
        copied = true;
      } else {
        input.focus();
        input.select();
        copied = document.execCommand("copy");
      }
    } catch (error) {
      copied = false;
    }
    panel.querySelector(".course-player-copy-status").textContent = copied ? "Link copied" : "Select the link to copy it";
  });
  listen(root, "keydown", function (event) {
    const panel = root.querySelector("[data-course-player-panel]:not([hidden])");
    if (!panel) return;
    if (event.key === "Escape") {
      event.stopPropagation();
      closePanel();
    } else if (event.key === "Tab") {
      const focusable = Array.from(panel.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  const fullscreenButton = root.querySelector("[data-course-player-fullscreen]");
  function updateFullscreenButton() {
    const active = document.fullscreenElement === root || state.expanded;
    root.classList.toggle("is-course-fullscreen", active);
    if (fullscreenButton) {
      fullscreenButton.setAttribute("aria-label", active ? "Exit fullscreen" : "Enter fullscreen");
      fullscreenButton.querySelector("span").textContent = active ? "Exit fullscreen" : "Fullscreen";
    }
    reveal();
  }
  if (fullscreenButton) {
    listen(fullscreenButton, "click", async function () {
      try {
        if (document.fullscreenElement === root) {
          await document.exitFullscreen();
        } else if (state.expanded) {
          state.expanded = false;
          root.classList.remove("is-course-expanded");
          document.body.classList.remove("course-player-expanded");
        } else if (root.requestFullscreen) {
          await root.requestFullscreen();
        } else {
          state.expanded = true;
          root.classList.add("is-course-expanded");
          document.body.classList.add("course-player-expanded");
        }
      } catch (error) {
        // Keep a usable expanded course view when native fullscreen is denied.
        state.expanded = true;
        root.classList.add("is-course-expanded");
        document.body.classList.add("course-player-expanded");
      }
      updateFullscreenButton();
    });
    listen(document, "fullscreenchange", updateFullscreenButton);
  }
  listen(document, "visibilitychange", function () {
    if (document.hidden) cancelHide();
    else reveal();
  });
  listen(document, "keydown", function (event) {
    if (event.key === "Escape" && state.expanded) {
      state.expanded = false;
      root.classList.remove("is-course-expanded");
      document.body.classList.remove("course-player-expanded");
      updateFullscreenButton();
    }
  });

  if ("IntersectionObserver" in window) {
    state.observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) reveal();
    }, { threshold: 0.6 });
    state.observer.observe(root);
  }

  if (state.playing) onPlay();
}

function disconnectCoursePlayer(root) {
  const state = root && coursePlayerStates.get(root);
  if (!state) return;
  clearTimeout(state.timer);
  if (state.observer) state.observer.disconnect();
  state.listeners.forEach(function (entry) { entry[0].removeEventListener(entry[1], entry[2]); });
  if (state.player.off) {
    state.playerListeners.forEach(function (entry) { state.player.off(entry[0], entry[1]); });
  }
  if (state.player.destroy) state.player.destroy();
  if (state.expanded) document.body.classList.remove("course-player-expanded");
  coursePlayerStates.delete(root);
}

window.CoursePlayerControls = {
  attach: attachCoursePlayer,
  disconnect: disconnectCoursePlayer,
  plyrControls: ["play-large", "play", "progress", "current-time", "mute", "volume", "captions", "settings", "pip", "airplay"]
};
